import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  GeraeteArtefakte,
  GeraeteAudit,
  Parametersatz,
  ParametersatzRequest,
  Parameterstand,
  Zugangsdaten,
  ZugangsdatenKlartext,
  ZugangsdatenRequest
} from '../Models/geraete-artefakte.model';

/**
 * Geräte-Parameterdateien und Zugangsdaten einer Messstelle.
 *
 * Alle Aufrufe laufen über den bestehenden AuthInterceptor und damit über die
 * vorhandene Anmeldung. Der Server verlangt für diese Endpunkte zusätzlich eine
 * verschlüsselte Verbindung und protokolliert jeden Zugriff auf sensible
 * Artefakte – auch abgelehnte.
 */
@Injectable({ providedIn: 'root' })
export class GeraeteArtefaktService {
  private baseUrl = environment.apiBaseUrl + '/geraeteartefakt';

  constructor(private http: HttpClient) { }

  /** Parameterdateien, Zugangsdaten-Kopfdaten und die Rechte des Benutzers. */
  getArtefakte(messstelleId: number): Observable<GeraeteArtefakte> {
    return this.http.get<GeraeteArtefakte>(`${this.baseUrl}/messstellen/${messstelleId}`);
  }

  // ── Parameterdateien ──

  createParametersatz(
    messstelleId: number,
    file: File,
    daten: {
      bezeichnung: string;
      geraeteHersteller?: string | null;
      geraeteTyp?: string | null;
      seriennummer?: string | null;
      beschreibung?: string | null;
      istVertraulich: boolean;
      kommentar?: string | null;
    }
  ): Observable<any> {

    const form = new FormData();

    form.append('file', file);
    form.append('bezeichnung', daten.bezeichnung);

    if (daten.geraeteHersteller) {
      form.append(
        'geraeteHersteller',
        daten.geraeteHersteller
      );
    }

    if (daten.geraeteTyp) {
      form.append(
        'geraeteTyp',
        daten.geraeteTyp
      );
    }

    if (daten.seriennummer) {
      form.append(
        'seriennummer',
        daten.seriennummer
      );
    }

    if (daten.beschreibung) {
      form.append(
        'beschreibung',
        daten.beschreibung
      );
    }

    if (daten.kommentar) {
      form.append(
        'kommentar',
        daten.kommentar
      );
    }

    form.append(
      'istVertraulich',
      String(daten.istVertraulich)
    );


    return this.http.post<any>(
      `${this.baseUrl}/messstellen/${messstelleId}/parametersaetze`,
      form
    );
  }

  updateParametersatz(
    parametersatzId: number,
    request: ParametersatzRequest
  ): Observable<Parametersatz> {
    return this.http.put<Parametersatz>(
      `${this.baseUrl}/parametersaetze/${parametersatzId}`,
      request
    );
  }

  deleteParametersatz(parametersatzId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/parametersaetze/${parametersatzId}`);
  }

  /**
   * Lädt einen neuen Stand hoch. Bestehende Stände bleiben erhalten – der neue
   * bekommt die nächste Versionsnummer.
   */
  uploadStand(
    parametersatzId: number,
    file: File,
    kommentar: string | null
  ): Observable<Parameterstand> {
    const form = new FormData();
    form.append('file', file, file.name);

    if (kommentar) {
      form.append('kommentar', kommentar);
    }

    return this.http.post<Parameterstand>(
      `${this.baseUrl}/parametersaetze/${parametersatzId}/staende`,
      form
    );
  }

  /**
   * Holt die Datei als Blob. Nötig, weil ein einfacher Link den Bearer-Token
   * nicht mitschickt – nur HttpClient-Aufrufe laufen durch den AuthInterceptor.
   * Der Server protokolliert den Download vor der Auslieferung.
   */
  downloadStand(standId: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/staende/${standId}/download`,
      { responseType: 'blob' }
    );
  }

  deleteStand(standId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/staende/${standId}`);
  }

  // ── Zugangsdaten ──

  createZugangsdaten(
    messstelleId: number,
    request: ZugangsdatenRequest
  ): Observable<Zugangsdaten> {
    return this.http.post<Zugangsdaten>(
      `${this.baseUrl}/messstellen/${messstelleId}/zugangsdaten`,
      request
    );
  }

  updateZugangsdaten(
    zugangsdatenId: number,
    request: ZugangsdatenRequest
  ): Observable<Zugangsdaten> {
    return this.http.put<Zugangsdaten>(
      `${this.baseUrl}/zugangsdaten/${zugangsdatenId}`,
      request
    );
  }

  deleteZugangsdaten(zugangsdatenId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/zugangsdaten/${zugangsdatenId}`);
  }

  /**
   * Entschlüsselt und liefert den Klartext.
   *
   * Bewusst POST: ein GET landet in Browserverlauf, Proxy-Logs und Vorschauen.
   * Jeder Aufruf erzeugt einen Eintrag im Sicherheitsprotokoll.
   */
  zeigeZugangsdaten(zugangsdatenId: number): Observable<ZugangsdatenKlartext> {
    return this.http.post<ZugangsdatenKlartext>(
      `${this.baseUrl}/zugangsdaten/${zugangsdatenId}/anzeigen`,
      {}
    );
  }

  // ── Sicherheitsprotokoll ──

  /** Audit-Trail der Messstelle, neueste Einträge zuerst. */
  getAudit(messstelleId: number, limit = 200): Observable<GeraeteAudit[]> {
    return this.http.get<GeraeteAudit[]>(
      `${this.baseUrl}/messstellen/${messstelleId}/audit?limit=${limit}`
    );
  }
}
